import { QuoteEmail } from "./email.model";
import { QuoteLocation } from "./quote-location.model";
import { Client } from "../../client/client.model";
import { BaseUser } from "../../user/base-user.model";
import { currencies } from "../../shared/currencies/currencies.const";
import { BaseStandardQuote } from "./quote-base-standard.model";
import { ContainerName } from "../../shared/container/container-name.type.const";
import { LocalCostLabel } from "./local-cost-label.model";
import { EquipmentQuantity } from "./equipment-quantity.model";
import { Insurance } from "./insurance.model";

export class FclQuote extends BaseStandardQuote {
  protected showChargesInTotalCurrency = false;
  protected chargesCurrency: string = null;

  constructor(
    protected id: number,
    protected displayId: string,
    protected groupId: number,
    protected ref: string,
    protected sender: BaseUser,
    protected createdBy: BaseUser,
    protected status: string,
    protected quoteType: string,
    protected direction: string,
    protected incotermType: string,
    protected clients: Client[],
    protected hasOrigin: boolean,
    protected hasDestination: boolean,
    protected originLocations: QuoteLocation[],
    protected destinationLocations: QuoteLocation[],
    protected localCostLabels: LocalCostLabel[],
    protected equipment: ContainerName[],
    protected equipmentQuantity: Map<string, EquipmentQuantity>,
    protected showGrandTotal = false,
    protected grandTotalCurrency: string,
    protected createdAt: Date,
    protected updatedAt: Date,
    protected updatedBy: BaseUser,
    protected openedAt: Date,
    protected validFrom: Date,
    protected validTo: Date,
    protected comment: string,
    protected showRateSurcharges = false,
    protected showCarrier = false,
    protected showServiceNameForLocalCharges = false,
    protected showRateName = false,
    protected showRateValidity = false,
    protected showInland = false,
    protected showTotal = false,
    protected totalCurrency = null,
    protected showInvoiceInfo = false,
    protected invoiceCurrency = false,
    protected groupRoutes = false,
    protected email: QuoteEmail,
    protected internalComment: string,
    protected customTerms: string,
    protected fileName: string,
    protected fileNameInternal: string,
    protected averageMargin: number,
    protected inquiry: string,
    protected source: string,
    protected insurance: Insurance | null,
    protected reasonOfDecline: string,
    protected input: string | null,
    protected offerLanguage: string | null,
    protected groupOriginSurcharges = false,
    protected groupDestinationSurcharges = false,
    protected groupOriginSurchargesCurrency = currencies.usd.value,
    protected groupDestinationSurchargesCurrency = currencies.usd.value,
    protected freightRateOrder: string[] = null,
  ) {
    super(
      id,
      displayId,
      groupId,
      ref,
      sender,
      createdBy,
      status,
      quoteType,
      clients,
      createdAt,
      updatedAt,
      updatedBy,
      openedAt,
      validFrom,
      validTo,
      email,
      internalComment,
      customTerms,
      fileName,
      fileNameInternal,
      averageMargin,
      inquiry,
      source,
      insurance,
      reasonOfDecline,
      input,
      offerLanguage,
      hasOrigin,
      hasDestination,
      originLocations,
      destinationLocations,
      localCostLabels,
    );
  }

  public getDirection(): string {
    return this.direction;
  }

  public setDirection(direction: string): void {
    this.direction = direction;
  }

  public getIncotermType(): string {
    return this.incotermType;
  }

  public setIncotermType(type: string): void {
    this.incotermType = type;
  }

  public getOriginLocations(): QuoteLocation[] {
    return this.originLocations;
  }

  public setOriginLocations(locations: QuoteLocation[]): void {
    this.originLocations = locations;
  }

  public getDestinationLocations(): QuoteLocation[] {
    return this.destinationLocations;
  }

  public setDestinationLocations(locations: QuoteLocation[]): void {
    this.destinationLocations = locations;
  }

  public getEquipment(): ContainerName[] {
    return this.equipment;
  }

  public setEquipment(eq: ContainerName[]): void {
    this.equipment = eq;
  }

  public getEquipmentQuantity(): Map<string, EquipmentQuantity> {
    return this.equipmentQuantity;
  }

  public setEquipentQuantity(
    equipmentQuantity: Map<string, EquipmentQuantity>,
  ) {
    this.equipmentQuantity = equipmentQuantity;
  }

  public getShowGrandTotal(): boolean {
    return this.showGrandTotal;
  }

  public setShowGrandTotal(showGrandTotal: boolean): void {
    this.showGrandTotal = showGrandTotal;
  }

  public getGrandTotalCurrency(): string {
    return this.grandTotalCurrency;
  }

  public getComment(): string {
    return this.comment;
  }

  public setComment(comment: string) {
    this.comment = comment;
  }

  public getShowRateSurcharges(): boolean {
    return this.showRateSurcharges;
  }

  public setShowRateSurcharges(param: boolean) {
    this.showRateSurcharges = param;
  }

  public getShowCarrier(): boolean {
    return this.showCarrier;
  }

  public setShowCarrier(param: boolean) {
    this.showCarrier = param;
  }

  public setShowServiceNameForLocalCharges(param: boolean) {
    this.showServiceNameForLocalCharges = param;
  }

  public getShowServiceNameForLocalCharges(): boolean {
    return this.showServiceNameForLocalCharges;
  }

  public setShowRateName(param: boolean) {
    this.showRateName = param;
  }

  public getShowRateName(): boolean {
    return this.showRateName;
  }

  public setShowRateValidity(param: boolean) {
    this.showRateValidity = param;
  }

  public getShowRateValidity(): boolean {
    return this.showRateValidity;
  }

  public getShowInland(): boolean {
    return this.showInland;
  }

  public setShowInland(param: boolean) {
    this.showInland = param;
  }

  public getShowTotal(): boolean {
    return this.showTotal;
  }

  public setShowTotal(param: boolean) {
    this.showTotal = param;
  }

  public getShowChargesInTotalCurrency(): boolean {
    return this.showChargesInTotalCurrency;
  }

  public setShowChargesInTotalCurrency(param: boolean) {
    this.showChargesInTotalCurrency = param;
  }

  public getChargesCurrency(): string {
    return this.chargesCurrency;
  }

  public setChargesCurrency(param: string) {
    this.chargesCurrency = param;
  }

  public getGroupRoutes(): boolean {
    return this.groupRoutes;
  }

  public setGroupRoutes(param: boolean) {
    this.groupRoutes = param;
  }

  public getTotalCurrency(): string {
    return this.totalCurrency;
  }

  public setTotalCurrency(param: string) {
    this.totalCurrency = param;
  }

  public getShowInvoiceInfo(): boolean {
    return this.showInvoiceInfo;
  }

  public setShowInvoiceInfo(param: boolean) {
    this.showInvoiceInfo = param;
  }

  public getInvoiceCurrency(): boolean {
    return this.invoiceCurrency;
  }

  public setInvoiceCurrency(param: boolean) {
    this.invoiceCurrency = param;
  }

  public getGroupOriginSurcharges(): boolean {
    return this.groupOriginSurcharges;
  }

  public setGroupOriginSurcharges(param: boolean) {
    this.groupOriginSurcharges = param;
  }

  public getGroupDestinationSurcharges(): boolean {
    return this.groupDestinationSurcharges;
  }

  public setGroupDestinationSurcharges(param: boolean) {
    this.groupDestinationSurcharges = param;
  }

  public getGroupOriginSurchargesCurrency(): string {
    return this.groupOriginSurchargesCurrency;
  }

  public setGroupOriginSurchargesCurrency(param: string) {
    this.groupOriginSurchargesCurrency = param;
  }

  public getGroupDestinationSurchargesCurrency(): string {
    return this.groupDestinationSurchargesCurrency;
  }

  public setGroupDestinationSurchargesCurrency(param: string) {
    this.groupDestinationSurchargesCurrency = param;
  }

  public getFreightRateOrder(): string[] {
    return this.freightRateOrder;
  }
  public setFreightRateOrder(param: string[]) {
    this.freightRateOrder = param;
  }
}
